import { KEEPALIVE_INTERVAL_MS, VIDEOROOM_PLUGIN } from './constants';
import { markNetworkDrop, markNetworkRecovered } from './networkDrop';
import type { JanusCallRefs, JanusCallSetters } from './sessionRefs';
import type { SendJanusFn } from './types';
import { logJanus, txId } from './utils';
import { getJanusWebSocketErrorMessage } from './wsError';

function isDuringMediaResync(r: JanusCallRefs): boolean {
  return (
    r.signalingResyncInFlightRef.current ||
    r.mediaReconnectPendingRef.current ||
    r.networkDropPendingRef.current
  );
}

export interface SignalingHandlerDeps {
  sendJanus: SendJanusFn;
  sendToHandle: (
    handleId: number,
    body: Record<string, unknown>,
    jsep?: RTCSessionDescriptionInit,
    transaction?: string
  ) => void;
  handleJanusEvent: (data: Record<string, unknown>) => Promise<void>;
  routeTrickle: (data: Record<string, unknown>) => void;
  cleanupCall: () => void;
  cleanupWebSocket: () => void;
  cancelWebSocketReconnect: () => void;
  rejectPending: (message: string) => void;
  isActiveCallSession: () => boolean;
}

export function routeTrickle(r: JanusCallRefs, data: Record<string, unknown>): void {
  const candidate = data.candidate as RTCIceCandidateInit | { completed?: boolean } | undefined;
  const isCompleted = candidate && 'completed' in candidate && candidate.completed;
  if (!candidate || isCompleted) return;

  const ice = new RTCIceCandidate(candidate as RTCIceCandidateInit);
  const senderHandle = data.sender as number | undefined;

  if (senderHandle === r.handleIdRef.current && r.publisherPcRef.current) {
    if (r.publisherPcRef.current.remoteDescription) {
      r.publisherPcRef.current.addIceCandidate(ice).catch(() => undefined);
    } else {
      r.publisherPendingCandidatesRef.current.push(ice);
    }
    return;
  }

  const sub = r.subscriberFeedsRef.current.find((s) => s.handleId === senderHandle);
  if (sub?.pc) {
    if (sub.pc.remoteDescription) {
      sub.pc.addIceCandidate(ice).catch(() => undefined);
    } else {
      sub.pendingCandidates.push(ice);
    }
  } else if (sub) {
    sub.pendingCandidates.push(ice);
  }
}

export function attachWebSocketHandlers(
  ws: WebSocket,
  r: JanusCallRefs,
  set: JanusCallSetters,
  deps: SignalingHandlerDeps
): void {
  ws.onopen = () => {
    r.intentionalWsCloseRef.current = false;

    if (deps.isActiveCallSession()) {
      markNetworkRecovered(r);
    }

    if (r.ghostSessionIdRef.current != null) {
      deps.sendJanus({
        janus: 'destroy',
        session_id: r.ghostSessionIdRef.current,
        transaction: txId('destroy-ghost'),
      });
      r.ghostSessionIdRef.current = null;
    }

    deps.sendJanus({ janus: 'create', transaction: 'create' });
  };

  ws.onmessage = async (event) => {
    const data = JSON.parse(event.data) as Record<string, unknown>;

    if (data.janus === 'destroyed') {
      const destroyedSessionId = data.session_id as number | undefined;
      if (
        destroyedSessionId != null &&
        r.sessionIdRef.current != null &&
        destroyedSessionId !== r.sessionIdRef.current
      ) {
        return;
      }
      if (destroyedSessionId != null && destroyedSessionId === r.sessionIdRef.current) {
        deps.cleanupCall();
        r.onRemoteCallEndedRef.current?.();
      }
      return;
    }

    if (data.janus === 'success') {
      const transaction = data.transaction as string;

      if (transaction === 'create') {
        r.sessionIdRef.current = (data.data as { id: number }).id;

        r.keepAliveIntervalRef.current = setInterval(() => {
          if (r.wsRef.current?.readyState === WebSocket.OPEN && r.sessionIdRef.current) {
            deps.sendJanus({
              janus: 'keepalive',
              session_id: r.sessionIdRef.current,
              transaction: 'ka',
            });
          }
        }, KEEPALIVE_INTERVAL_MS);

        const attachTx = r.pendingAttachAfterCreateRef.current;
        r.pendingAttachAfterCreateRef.current = 'attach';
        deps.sendJanus({
          janus: 'attach',
          session_id: r.sessionIdRef.current,
          plugin: VIDEOROOM_PLUGIN,
          transaction: attachTx,
        });
      } else if (transaction === 'attach' || transaction === 'attach-reconnect') {
        r.handleIdRef.current = (data.data as { id: number }).id;
        r.isSessionReadyRef.current = true;
        if (r.callStatusRef.current !== 'oncall') {
          set.setCallStatus('ready');
        }
        r.ensureSessionResolveRef.current?.();
        r.ensureSessionResolveRef.current = null;
        r.ensureSessionRejectRef.current = null;
      } else if (transaction?.startsWith('attach-sub-')) {
        const handleId = (data.data as { id: number }).id;
        const publisherId = r.pendingSubscriberAttachesRef.current.get(transaction);
        if (publisherId != null) {
          r.pendingSubscriberAttachesRef.current.delete(transaction);
          r.subscriberFeedsRef.current.push({
            handleId,
            feed: publisherId,
            pc: null,
            pendingCandidates: [],
            isMediaStarted: false,
          });

          deps.sendToHandle(
            handleId,
            {
              request: 'join',
              room: r.roomIdRef.current,
              ptype: 'subscriber',
              feed: publisherId,
              private_id: r.myPrivateIdRef.current ?? undefined,
            },
            undefined,
            `join-sub-${publisherId}`
          );
        }
      }
    }

    if (data.janus === 'event') {
      await deps.handleJanusEvent(data);
    }

    if (data.janus === 'trickle') {
      deps.routeTrickle(data);
    }

    if (data.janus === 'hangup') {
      const senderHandle = data.sender as number | undefined;
      const sub = r.subscriberFeedsRef.current.find((s) => s.handleId === senderHandle);
      if (sub) {
        sub.pc?.close();
        r.subscriberFeedsRef.current = r.subscriberFeedsRef.current.filter(
          (s) => s.handleId !== senderHandle
        );
        r.subscribedFeedIdsRef.current.delete(sub.feed);

        if (r.subscriberFeedsRef.current.length === 0) {
          set.setRemoteStream(null);
          if (isDuringMediaResync(r)) {
            logJanus('SUB', 'subscriber hangup during resync — wait for reconnect');
          } else {
            logJanus('SUB', 'subscriber hangup — peer left, end call');
            r.onRemoteCallEndedRef.current?.();
          }
        }
      } else if (senderHandle === r.handleIdRef.current) {
        if (!r.intentionalWsCloseRef.current && deps.isActiveCallSession()) {
          logJanus('RECONNECT', 'publisher hangup during call — reconnect (mobile does not cleanup)');
          r.wsReconnectAttemptsRef.current = 0;
          r.scheduleWebSocketReconnectRef.current(true);
        } else {
          logJanus('PUB', 'publisher hangup — cleanup');
          deps.cleanupCall();
        }
      }
    }
  };

  ws.onerror = () => {
    if (deps.isActiveCallSession() && !r.intentionalWsCloseRef.current) {
      if (r.keepAliveIntervalRef.current) clearInterval(r.keepAliveIntervalRef.current);
      r.isSessionReadyRef.current = false;
      r.scheduleWebSocketReconnectRef.current();
      return;
    }

    const message = getJanusWebSocketErrorMessage(r.translateCallRef.current);
    logJanus('WS', 'connection error (initial session)', {
      url: r.signalingUrlRef.current,
      cocCoc: typeof navigator !== 'undefined' && /coc_coc|coccoc/i.test(navigator.userAgent),
    });
    set.setError(message);
    deps.rejectPending(message);
    r.intentionalWsCloseRef.current = true;
    deps.cleanupWebSocket();
    deps.cleanupCall();
  };

  ws.onclose = () => {
    if (r.keepAliveIntervalRef.current) clearInterval(r.keepAliveIntervalRef.current);
    r.wsRef.current = null;
    r.isSessionReadyRef.current = false;

    if (r.intentionalWsCloseRef.current) return;

    if (deps.isActiveCallSession()) {
      markNetworkDrop(r, deps.isActiveCallSession, deps.cancelWebSocketReconnect, 'websocket onclose');
      r.wsReconnectAttemptsRef.current = 0;
      r.scheduleWebSocketReconnectRef.current(true);
      return;
    }

    r.isJoinedRef.current = false;
    set.setCallStatus('idle');
  };
}
